import { api, RouterInput, RouterOutput } from "../../services/rpc/RpcProvider";
import { useToast } from "@introist/react-foundation/v2";
import { useCallback } from "react";

export type Task = RouterOutput["tasks"]["find"];
export type TaskCreate = RouterInput["tasks"]["create"];
export type TaskUpdate = RouterInput["tasks"]["update"];

export const useTasks = () => {
  const { data: tasks, isLoading } = api.tasks.list.useQuery({});
  return { tasks, loading: isLoading };
};

export const useTask = (id: string) => {
  const { data: task } = api.tasks.find.useQuery({ id });
  return { task };
};

export const useTaskActions = () => {
  const toast = useToast();
  const apiUtils = api.useContext();

  const listOp = apiUtils.tasks.list;
  const findOp = apiUtils.tasks.find;

  const createOp = api.tasks.create.useMutation();
  const updateOp = api.tasks.update.useMutation();
  const deleteOp = api.tasks.delete.useMutation();

  const create = useCallback(
    async (req: TaskCreate) => {
      return createOp
        .mutateAsync(req)
        .then(async created => {
          await listOp.refetch();
          return created;
        })
        .catch(e => {
          toast.error("Failed to create Task");
          throw e;
        });
    },
    [createOp, listOp, toast]
  );

  const update = useCallback(
    async (req: TaskUpdate) => {
      return updateOp
        .mutateAsync(req)
        .then(async updated => {
          await listOp.refetch();
          await findOp.refetch({ id: req.id });
          return updated;
        })
        .catch(e => {
          toast.error("Failed to update Task");
          throw e;
        });
    },
    [updateOp, listOp, findOp, toast]
  );

  const remove = useCallback(
    async (id: string) => {
      await deleteOp
        .mutateAsync({ id })
        .then(async () => {
          await listOp.refetch();
          toast.success("Task deleted");
        })
        .catch(() => {
          toast.error("Failed to delete Task");
        });
    },
    [deleteOp, toast, listOp]
  );

  return { create, update, remove };
};
